import { jsx as _jsx } from "react/jsx-runtime";
import { cn } from '../lib/cn';
/**
 * The id every shell puts on its `<main>`, and the only thing {@link SkipToMainLink} points at.
 *
 * One exported constant rather than the string `"main-content"` written in five places, because
 * the two halves of a skip link fail *silently* when they disagree: the anchor still renders, it
 * still takes focus, and pressing Enter does nothing at all. There is no error and no visual
 * difference — the keyboard user simply lands back where they started and tabs through the whole
 * rail again. A typo in one of five string literals is exactly the defect this constant removes,
 * and it is why `SkipToMainLink` takes no `href` prop: there is nothing else it could usefully
 * point at.
 */
export const MAIN_CONTENT_ID = 'main-content';
/**
 * **"Skip to main content"** — the first focusable element of every H2HSpark layout
 * (`PLT-007` AC-3).
 *
 * ## What it is for
 *
 * Every one of this platform's shells opens with chrome a keyboard user has to get past before
 * they reach the screen: the consoles' rail is five to fifteen navigation items plus an account
 * block, the member app's desktop header is five, the marketing site's header is however many
 * links the CMS publishes. Without this link that chrome is re-traversed on *every* page, because
 * a single-page navigation does not reset the tab order to anything useful. This is the standard
 * escape hatch, and WCAG 2.4.1 asks for it by name.
 *
 * ## Why it is `sr-only` until focused rather than always hidden
 *
 * A skip link that is `display:none` or `visibility:hidden` is not focusable at all, so it helps
 * nobody. `sr-only` keeps it in the accessibility tree and in the tab order while taking it out
 * of the visual design, and `focus:not-sr-only` puts it back on screen the moment it is tabbed
 * to — which is the one moment anybody needs to see it.
 *
 * ## Why the focus styling is this heavy
 *
 * Everything after `focus:not-sr-only` exists so the revealed link is *readable*. The failure
 * mode is not that it stays hidden; it is that it appears with no background behind it, or
 * underneath a `sticky` header, or scrolled off the top of a long page — a link that is
 * technically visible and that no sighted keyboard user can actually read. So:
 *
 *   - `focus:fixed` with `focus:left-4 focus:top-4` pins it to the viewport rather than to the
 *     document. `sr-only` is itself `position:absolute` and `not-sr-only` resets that to
 *     `static`, so the position has to be restated on focus or the offsets do nothing. `fixed`
 *     over `absolute` because the nearest positioned ancestor differs in all three shells and an
 *     absolute link would land somewhere different in each.
 *   - `focus:z-modal` (1000) clears the shells' own layers — the consoles' rail is `sticky` and
 *     the member header is `z-30`. `z-nav` (100) and `z-sticky` (200) are not enough.
 *   - `focus:bg-card` plus `focus:border` plus `focus:shadow-lg` give it something opaque to sit
 *     on, since it is drawn over whatever the page already painted there.
 *
 * The ring is the visible focus indicator this control keeps in exchange for the outline it
 * removes — the same `ring-2 ring-ring ring-offset-2` every other focusable primitive in this
 * package uses, so the one control that only ever appears while focused does not have a focus
 * treatment of its own invention.
 *
 * ## Where it goes
 *
 * First in the DOM order of the layout — before the header, before the rail, before anything
 * else focusable. Not first *visually*: it is `sr-only` until it is tabbed to, and the tab order
 * is what "first" means here. Its target is the `<main>`, which carries `id={MAIN_CONTENT_ID}`
 * and `tabIndex={-1}`; without the negative tabindex a browser scrolls the fragment into view but
 * leaves focus on the link, so the next Tab goes straight back into the navigation and the link
 * has skipped nothing.
 */
export function SkipToMainLink({ 
/** Extra classes for a shell that needs to nudge the revealed position. Rarely needed. */
className, } = {}) {
    return (_jsx("a", { href: `#${MAIN_CONTENT_ID}`, className: cn('sr-only', 'focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-modal', 'focus:inline-flex focus:items-center focus:rounded-md focus:border focus:border-border', 'focus:bg-card focus:px-4 focus:py-2 focus:shadow-lg', 'focus:font-brand focus:text-sm focus:font-semibold focus:text-foreground', 
        // The outline this removes is replaced in the same breath, and by the package's own ring
        // rather than a bespoke one — see the header.
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background', className), children: "Skip to main content" }));
}
//# sourceMappingURL=skip-to-main-link.js.map